import { BaseTextField } from '@components/BaseTextField';
import StyledDateRangePicker from '@components/date-range-select/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import PopUpWarning from '@components/PopUpWarning';
import { potentialGroupContraventionController } from '@controllers/index';
import { IGetGroupPotentialContraventionIds } from '@EcamModel/controllers/IGroupPotentialContraventionHttpController';
import usePopUp from '@hooks/usePopUp';
import { Autocomplete, Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { GroupHeader, GroupItems } from '@pages/cameras/add-new';
import { Filter } from '@pages/cameras/list';
import { usePotentialContravention } from '@pages/verification/pc-details/hooks/PotentialContraventionProvider';
import { useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { contraventionTypeEventLog } from '../configs';
import ArrowRight from '@assets/jsx-icon/ArrowRight';

type Props = {
    filter: Filter;
    handleResetFilter: () => void;
    onSetPartialEventLog: (e: Partial<Filter>) => void;
};

export default function FilterEventLogComponents(props: Props) {
    const { filter, onSetPartialEventLog, handleResetFilter } = props;
    const params = useParams<{ contraventionId: string }>();
    const contraventionId = params.contraventionId;

    const navigate = useNavigate();
    const location = useLocation();
    const popupConfirmMoveToBespokeVerify = usePopUp();
    const { groupPc, setGroupPcIds, contraventionDateFrom, contraventionDateTo } = usePotentialContravention();

    const [isLoadingConfirm, setIsLoadingConfirm] = useState<boolean>(false);

    const isVerificationContraventionRoute = location.pathname === `/verification/contravention/${contraventionId}`;

    const handleOnConfirm = () => {
        if (!groupPc) return;
        setIsLoadingConfirm(true);

        potentialGroupContraventionController
            .doMoveGroupPCNToBespokeVerification({ GroupPotentialContraventionId: Number(contraventionId) })
            .then(async (_) => {
                navigate(`/bespoke-verification/contravention/${contraventionId}`);
                popupConfirmMoveToBespokeVerify.onClose();

                const props: IGetGroupPotentialContraventionIds = {
                    Step: 1,
                    ZoneId: groupPc?.PotentialContraventions?.[0].ZoneId!,
                    ContraventionDate: { From: contraventionDateFrom!, To: contraventionDateTo! },
                    IsObscuredPlate: true,
                };
                if (!contraventionDateFrom || !contraventionDateTo) {
                    delete props.ContraventionDate;
                }
                await potentialGroupContraventionController.getGroupPotentialContraventionIds(props).then((res) => {
                    setGroupPcIds(res.Ids.map((id) => ({ Id: id })));
                });
            })
            .catch((_) => {
                setIsLoadingConfirm(false);
            });
    };

    return (
        <>
            <FilterRoot
                rightTitleContent={
                    <Grid2 container sx={{ minWidth: 400 }}>
                        <StyledDateRangePicker
                            value={{
                                startDate: filter?.from || undefined,
                                endDate: filter?.to || undefined,
                            }}
                            onChange={(value) => {
                                onSetPartialEventLog({
                                    from: value.startDate,
                                    to: value.endDate,
                                    search: filter.search,
                                });
                            }}
                            dateReset={{ startDate: undefined, endDate: undefined }}
                            label={'Select date'}
                        />
                    </Grid2>
                }
                filterChild={
                    <Stack direction={'row'} width={'100%'} justifyContent={'space-between'} spacing={2}>
                        <Stack width={'50%'}>
                            <BaseTextField
                                placeholder={'Search name created by'}
                                value={filter.search}
                                onChange={(e) => {
                                    onSetPartialEventLog({
                                        search: e.target.value,
                                        from: filter.from,
                                        to: filter.to,
                                    });
                                }}
                                maxLength={50}
                                InputProps={{
                                    endAdornment: (
                                        <FiSearch
                                            color="disabled"
                                            size={16}
                                            style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                        />
                                    ),
                                }}
                            />
                        </Stack>
                        <Stack width={'50%'}>
                            <Autocomplete
                                id="grouped-demo"
                                value={filter.verifierEventLog || null}
                                options={contraventionTypeEventLog}
                                groupBy={(option) => option.GroupTextKey}
                                getOptionLabel={(option) => option.Name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        padding: '9.95px 14px !important',
                                    },
                                    '& .MuiButtonBase-root': {
                                        marginRight: '5px !important',
                                    },
                                    '& .MuiAutocomplete-inputRoot': {
                                        '& 	.MuiAutocomplete-input': {
                                            padding: '0px 0px',
                                        },
                                    },
                                }}
                                renderGroup={(params) => (
                                    <li key={params.key}>
                                        <GroupHeader>
                                            <Typography variant="h6" color={(theme) => theme.palette.grey[600]}>
                                                {params.group}
                                            </Typography>
                                        </GroupHeader>
                                        <GroupItems>{params.children}</GroupItems>
                                    </li>
                                )}
                                clearIcon
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputLabelProps={{ shrink: true }}
                                        label={'Event types'}
                                        placeholder="All types"
                                    />
                                )}
                                onChange={(e, val) => {
                                    if (!val) return;
                                    onSetPartialEventLog({
                                        verifierEventLog: val,
                                    });
                                }}
                            />
                        </Stack>
                    </Stack>
                }
                handleResetFilter={handleResetFilter}
            />
            <PopUpWarning
                {...popupConfirmMoveToBespokeVerify}
                isLoading={isLoadingConfirm}
                title="Confirm"
                message="Are you sure you want to move this group to Bespoke Verification?"
                onConfirm={handleOnConfirm}
            />
        </>
    );
}
